import { useSceneEvents } from "@/components/common/scene-events-context";
import { isRoomSection } from "@/components/ui/tree/capture-tree/capture-tree-discriminator";
import { ModeNames } from "@/modes/mode";
import { setActiveCad } from "@/store/cad/cad-slice";
import { addIElementsToStore } from "@/store/ielements-store-utilities";
import {
  selectActiveElementForAnnotation,
  selectModeFromElement,
  selectModeName,
} from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { selectMarkupImagePointWorldPosition } from "@/store/select-markup-image-point-world-position";
import { setActiveElement } from "@/store/selections-slice";
import { AppStore } from "@/store/store";
import { useAppStore } from "@/store/store-hooks";
import {
  collapseAllAnnotations,
  setAnnotationExpansion,
} from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { selectCameraViewInThreeSpace } from "@/utils/cam-view";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericAnnotation,
  isIElementGenericAnnotation,
  isIElementImg360,
  isIElementModel3dStream,
  isIElementTimeTravelSection,
  MarkupIElement,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectBestViewForAnnotation,
  selectIElement,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback } from "react";
import { Vector3 } from "three";

export type GoToAnnotationFunction = (markup: MarkupIElement) => void;

/**
 * @param markup to go to
 * @returns a function that will update the app state/scene to go to the specified markup
 */
export function useGoToAnnotation(): GoToAnnotationFunction {
  const { lookAt, setCameraView } = useSceneEvents();
  const store = useAppStore();

  const { projectApiClient } = useApiClientContext();

  const getAnnotation = useCallback(
    async (markup: MarkupIElement): Promise<IElementGenericAnnotation> => {
      let annotation = selectIElement(markup.parentId)(store.getState());

      // If the area that contains this markup is not loaded yet we don't have its parent
      // The parent elements of the markup needs to be loaded to be able to visualize it
      if (!annotation) {
        const parentTree = await projectApiClient.getAllIElements({
          descendantIds: [markup.id],
        });
        addIElementsToStore(parentTree, store.dispatch);
        annotation = selectIElement(markup.parentId)(store.getState());

        // Load all data of the current room, if it exists, otherwise the 360 image won't
        // be loaded for polygon markups since it's not in the list of ancestors
        const parentSection = selectAncestor(
          annotation,
          (e) => isRoomSection(e) || isIElementTimeTravelSection(e),
        )(store.getState());
        if (parentSection) {
          const iElements = await projectApiClient.getAllIElements({
            ancestorIds: [parentSection.id],
          });
          addIElementsToStore(iElements, store.dispatch);
        }
      }

      assert(
        annotation && isIElementGenericAnnotation(annotation),
        "A markup should always be part of an annotation",
      );
      return annotation;
    },
    [projectApiClient, store],
  );

  return useCallback(
    async (markup: MarkupIElement) => {
      const annotation = await getAnnotation(markup);

      const activeElement = selectActiveElementForAnnotation(annotation)(
        store.getState(),
      );
      assert(
        activeElement,
        "Failed to compute the best element to use to look at the annotation",
      );

      const isPano = isIElementImg360(activeElement);
      const isCadModel = isIElementModel3dStream(activeElement);

      const bestModeForElement = selectModeFromElement(
        activeElement,
        annotation.id,
      )(store.getState());
      assert(
        bestModeForElement,
        "Failed to compute the best mode to use to look at the annotation",
      );

      if (isCadModel) {
        store.dispatch(setActiveCad(activeElement.id));
      } else {
        store.dispatch(setActiveElement(activeElement.id));
      }

      const savedView = selectBestViewForAnnotation(annotation)(
        store.getState(),
      );

      const currentMode = selectModeName(store.getState());

      const isPanoInSplitMode = currentMode === "split" && isPano;

      if (currentMode === bestModeForElement || isPanoInSplitMode) {
        // If the app is already in the best mode to look at the annotation move the camera to look at the annotation
        const position = new Vector3().fromArray(
          selectIElementWorldPosition(annotation.id)(store.getState()),
        );

        const point = selectMarkupImagePointWorldPosition(
          markup,
          activeElement,
        )(store.getState());

        // and expand only the annotation to look at
        store.dispatch(collapseAllAnnotations());
        store.dispatch(
          setAnnotationExpansion({ id: markup.id, expanded: true }),
        );

        const isCadInOverview = bestModeForElement === "overview" && isCadModel;

        // If we are targeting walk mode with a pano, synchronize the scene filter too
        setupFlyWalkMode(bestModeForElement, isPano, store);
        setupFlyWalkCadOverview(isCadInOverview, store);

        if (savedView) {
          const cameraView = selectCameraViewInThreeSpace(savedView)(
            store.getState(),
          );
          assert(cameraView, "At this point cameraView should be defined");
          setCameraView.emit(cameraView);
        } else if (point) {
          lookAt.emit(point.clone());
        } else {
          lookAt.emit(position);
        }
      } else {
        // Transition to the best mode to look at the annotation
        let sceneFilter = SceneFilter.PointCloud;
        if (isCadModel) {
          sceneFilter = SceneFilter.Cad;
        } else if (isPano) {
          sceneFilter = SceneFilter.Pano;
        }
        store.dispatch(
          changeMode({
            mode: bestModeForElement,
            initialState: {
              lookAtId: markup.id,
              scene: sceneFilter,
            },
          }),
        );
      }
    },
    [lookAt, setCameraView, store, getAnnotation],
  );
}

function setupFlyWalkMode(
  bestModeForElement: ModeNames,
  isPano: boolean,
  store: AppStore,
): void {
  if (
    bestModeForElement === "walk" &&
    isPano &&
    selectFlyWalkSceneFilter(store.getState()) !== SceneFilter.Pano
  ) {
    store.dispatch(setFlyWalkSceneFilter(SceneFilter.Pano));
  }
}

function setupFlyWalkCadOverview(
  isCadInOverview: boolean,
  store: AppStore,
): void {
  if (
    isCadInOverview &&
    selectFlyWalkSceneFilter(store.getState()) !== SceneFilter.Cad &&
    selectFlyWalkSceneFilter(store.getState()) !== SceneFilter.Overlay
  ) {
    store.dispatch(setFlyWalkSceneFilter(SceneFilter.Cad));
  }
}
